/**
 * PublicLayout - 公开页共享布局（navbar + 可选 footer）。
 *
 * 消除 PricingPage / LegalPage 及新内容页里重复的 navbar JSX。
 * navbar 含：Logo + 语言切换 + Clerk 登录/注册控件 + 已登录的 Workbench 入口。
 *
 * homeNav（博客页用）：顶栏中段追加与首页 Navbar 一致的五链接快捷导航，
 * 锚点项指回首页对应区块（/:lang#how 等）。
 *
 * 用法：
 *   <PublicLayout footer={<MyFooter />}>
 *     <main>页面主体</main>
 *   </PublicLayout>
 *
 * footer 可选（LegalPage 用 .legal-back 内联回链，PricingPage 用 .pricing-footer，
 * 新内容页用统一 footer）。
 */

import type { ReactNode } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, type Location } from "react-router-dom";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
} from "@clerk/clerk-react";
import LocaleSwitcher from "./LocaleSwitcher";
import Logo from "./Brand";
import { CreditCard, Mail } from "./icons";
import i18n from "../i18n";
import { LocalizedLink, useLang } from "../lib/lang";
import { USER_BUTTON_POPOVER_CARD } from "../lib/clerkAppearance";

export default function PublicLayout({
  children,
  footer,
  homeNav = false,
}: {
  children: ReactNode;
  footer?: ReactNode;
  /** 博客页传 true：顶栏中段渲染与首页一致的快捷导航（社区/教程/定价/博客/问答） */
  homeNav?: boolean;
}) {
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  /** 打开「我的订阅」路由态弹窗（与工作台/首页账号菜单一致）：
   * SPA 导航带 state.background，当前页保持挂载，弹窗叠加其上（见 App.tsx）。 */
  function openBilling() {
    const state = location.state as { background?: Location } | null;
    navigate(`/${lang}/workbench/billing`, {
      state: { background: state?.background ?? location },
    });
  }

  // 英文独占路由（/blog）不在 /:lang 下，LangSync 不挂载：挂载时把 i18next
  // 与 <html lang> 对齐到记忆语言（useLang 无前缀时回退 detectLang →
  // localStorage），保证硬加载博客页后顶栏菜单仍按用户所选语言渲染。
  // 带前缀的公共页上该值与 URL 参数一致，与 LangSync 的同步幂等无冲突。
  useEffect(() => {
    if (i18n.language !== lang) void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  }, [lang]);
  // 默认公共页（定价/法律/guides）顶栏只保留 Logo + 语言/认证控件；
  // 博客页传 homeNav 时与首页 Navbar 同款五链接——锚点项指回首页对应区块
  // （博客路由在 /:lang 之外，裸 #how 在本页无目标元素），博客项保持根级 /blog。
  const navLinks = homeNav
    ? [
        { href: `/${lang}#community`, label: t("landing.nav.community") },
        { href: `/${lang}#how`, label: t("landing.nav.how") },
        { href: `/${lang}#pricing`, label: t("landing.nav.pricing") },
        { href: "/blog", label: t("nav.blog") },
        { href: `/${lang}#faq`, label: t("landing.nav.faq") },
      ]
    : [];
  return (
    <>
      <nav className="navbar">
        <Logo />
        {navLinks.length > 0 && (
          <div className="nav-links">
            {navLinks.map((l) => (
              <Link key={l.href} to={l.href}>
                {l.label}
              </Link>
            ))}
          </div>
        )}
        <div className="nav-auth">
          <LocaleSwitcher />
          <SignedIn>
            <LocalizedLink to="/workbench/generate" className="nav-btn nav-generate">
              {t("common.workbench")}
            </LocalizedLink>
            {/* 与工作台侧边栏账号菜单一致：我的订阅 / 帮助中心。一律用
                UserButton.Action + SPA navigate（Clerk 菜单 Link 是原生 <a>，
                点击会整页重载；帮助中心指向 FAQ，mailto 直链有缺陷不用）；
                弹出菜单加宽，与工作台/首页账号弹层一致。 */}
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  userButtonPopoverCard: USER_BUTTON_POPOVER_CARD,
                },
              }}
            >
              <UserButton.MenuItems>
                <UserButton.Action
                  label={t("nav.billing")}
                  labelIcon={<CreditCard size={16} />}
                  onClick={openBilling}
                />
                <UserButton.Action
                  label={t("nav.helpCenter")}
                  labelIcon={<Mail size={16} />}
                  onClick={() => navigate(`/${lang}/faq`)}
                />
              </UserButton.MenuItems>
            </UserButton>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button className="nav-btn nav-signin">{t("common.signIn")}</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="nav-btn nav-signup">{t("common.signUp")}</button>
            </SignUpButton>
          </SignedOut>
        </div>
      </nav>
      {children}
      {footer}
    </>
  );
}
